import { ReactElement, ReactNode } from "react";
import {
  render,
  MARK_LINK,
  NODE_UL,
  NODE_OL,
  NODE_HEADING,
  NODE_PARAGRAPH,
  RenderOptions,
  NODE_IMAGE,
} from "storyblok-rich-text-react-renderer";

import LinkComponent from "@/components/elements/LinkComponent";
import Heading from "../Heading";

import { HeadingLevel } from "../Heading/types";
import RichTextLink from "./RichTextLink";
import RichTextSocialLink from "./RichTextSocialLink";
import RichTextBlockquote from "./RichTextBlockquote";
import RichTextList from "./RichTextList";
import RichTextTable from "./RichTextTable";
import RichTextReferences from "./RichTextReferences";
import RichTextImageInfographic from "./RichTextImageInfographic";
import RichTextAccordion from "./RichTextAccordion";
import RichTextTableOfContents from "./RichTextTableOfContents";

import * as styles from "./styles.module.scss";
import RichTextVideo from "./RichTextVideo";
import RichTextSubscriberForm from "./RichTextSubscriberForm";

interface RemarkRichTextComponentsProps {
  linkWithIcon?: boolean;
  content?: Pick<StoryblokRichtext, "content">;
}

interface LinkProps {
  href: LinkElement["url"];
  type?: string;
  target?: string;
  children?: ReactNode;
}

type LinkElementProps = {
  title?: string;
  type: LinkElementType;
  url: string;
  with_icon?: boolean;
};

export function getLinkProps(link: LinkProps) {
  const { href, type } = link;

  const storyLink: LinkElementProps = { ...link, type: "internal", url: href };
  const urlLink: LinkElementProps = { ...link, type: "external", url: href };
  const emailLink: LinkElementProps = {
    ...link,
    type: "external",
    url: `mailto:${href}`,
  };

  switch (type) {
    case "story":
      return storyLink;
    case "url":
      return urlLink;
    case "asset":
      return urlLink;
    case "email":
      return emailLink;
    default:
      return { ...link, url: href } as LinkElementProps;
  }
}

const blokResolvers: PropType<RenderOptions, "blokResolvers"> = {
  element_link: (props: Partial<LinkElement>) => <RichTextLink {...props} />,
  element_socialLink: (props: Partial<SocialLinkElement>) => (
    <RichTextSocialLink {...props} />
  ),
  component_blockquote: (props: Partial<BlockquoteComponent>) => (
    <RichTextBlockquote {...props} />
  ),
  component_list: (props: Partial<ListComponent>) => (
    <RichTextList {...props} />
  ),
  component_table: (props: Partial<TableComponent>) => (
    <RichTextTable {...props} />
  ),
  component_referencesList: (props: Partial<ReferencesListComponent>) => (
    <RichTextReferences {...props} />
  ),
  component_imageInfographic: (props: Partial<ImageInfographicComponent>) => (
    <RichTextImageInfographic {...props} />
  ),
  component_accordion: (props: Partial<AccordionComponent>) => (
    <RichTextAccordion {...props} />
  ),
  component_subscriber_form: (props: Partial<SubscriberFormComponent>) => (
    <RichTextSubscriberForm {...props} />
  ),
  component_tableOfContents: (props: Partial<TableOfContentsComponent>) => (
    <RichTextTableOfContents {...props} />
  ),
  component_videoUrl: (props: Partial<VideoComponent>) => (
    <RichTextVideo {...props} />
  ),
};

const nodeResolvers: PropType<RenderOptions, "nodeResolvers"> = {
  [NODE_HEADING]: (children, props) => {
    const isChapter =
      Array.isArray(children) &&
      children.find((item) => item?.props?.className === "chapter");

    const chapterId = isChapter
      ? children[0]?.props?.children?.toLowerCase().replace(/\s/g, "-")
      : undefined;

    return (
      <Heading
        level={props.level as HeadingLevel}
        className={isChapter ? "chapter-heading" : ""}
        id={chapterId}
      >
        {children}
      </Heading>
    );
  },
  [NODE_UL]: (children) =>
    children ? (
      <ul className={styles.customUnorderedList}>{children}</ul>
    ) : null,
  [NODE_IMAGE]: (children, props) => {
    if (!props?.src) return null;
    const isGif = props?.src?.endsWith(".gif");

    return (
      <img
        src={props.src}
        alt={props.alt}
        className={isGif ? styles.customGif : undefined}
      />
    );
  },
  [NODE_OL]: (children) =>
    children ? <ol className={styles.customOrderedList}>{children}</ol> : null,
  [NODE_PARAGRAPH]: (children) =>
    children ? <p className={styles.customParagraph}>{children}</p> : null,
};

export function RemarkRichTextComponents({
  content,
  linkWithIcon,
}: RemarkRichTextComponentsProps): ReactElement {
  const elements = { ...content };

  return (
    <>
      {render(elements, {
        blokResolvers,
        nodeResolvers,
        markResolvers: {
          [MARK_LINK]: (
            children: ReactNode,
            props: { href?: string; target?: string; linktype?: string }
          ) => (
            <LinkComponent
              {...getLinkProps({
                ...props,
                href: props.href || "",
                type: props.linktype,
                children,
              })}
              with_icon={linkWithIcon}
            />
          ),
        },
      })}
    </>
  );
}
